<template>
  <div class="redis-admin-panel" v-loading="syncing">
    <section class="panel-header">
      <div>
        <h3>Redis Key 查询</h3>
        <p>Ford 后台 Redis 管理操作</p>
      </div>
      <el-tag effect="plain" type="warning">管理员</el-tag>
    </section>

    <section class="config-section">
      <div class="section-heading">
        <div>
          <h4>锁号同步</h4>
          <p>BlackList:Sn:* / blacklist</p>
        </div>
        <el-tag :type="syncStatusTag" effect="plain">{{ syncStatusText }}</el-tag>
      </div>

      <div class="operation-row">
        <div class="operation-title">
          <el-icon><Refresh /></el-icon>
          <span>同步国内/海外锁号</span>
        </div>
        <el-button
          type="primary"
          :icon="Refresh"
          :loading="syncing"
          @click="handleSyncBlacklist"
        >
          同步国内/海外锁号
        </el-button>
      </div>

      <div class="operation-row">
        <div class="operation-title">
          <el-icon><Refresh /></el-icon>
          <span>补齐黑名单接头配置</span>
        </div>
        <el-button
          type="success"
          plain
          :icon="Refresh"
          :loading="configRefreshing"
          @click="handleRefreshBlackSnConfigs"
        >
          后台补齐接头配置
        </el-button>
      </div>

      <el-alert
        v-if="syncError"
        class="sync-error"
        type="error"
        :closable="false"
        show-icon
        :title="syncError"
      />

      <div v-if="syncResult" class="summary-grid">
        <div
          v-for="item in summaryCards"
          :key="item.key"
          class="summary-card"
        >
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </div>

      <el-table
        v-if="summaryRows.length"
        :data="summaryRows"
        border
        size="small"
        class="summary-table"
      >
        <el-table-column prop="name" label="节点" width="86" />
        <el-table-column prop="total" label="总数" width="86" align="right" />
        <el-table-column prop="northAmericaCount" label="北美" width="86" align="right" />
        <el-table-column prop="nonNorthAmericaCount" label="非北美" width="86" align="right" />
        <el-table-column prop="uncachedFilialeCount" label="待确认" width="92" align="right" />
        <el-table-column prop="snapshotHash" label="快照 Hash" min-width="150">
          <template #default="{ row }">
            <span class="mono">{{ row.snapshotHash || '-' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Refresh } from '@element-plus/icons-vue';

import { refreshBlackSnConfigs, syncFordBlacklistServers } from '@/api/users';

const syncing = ref(false);
const configRefreshing = ref(false);
const syncResult = ref(null);
const syncError = ref('');

const localSummary = computed(() => syncResult.value?.localSummary || null);
const remoteSummary = computed(() => syncResult.value?.remoteSummary || null);

const syncStatusText = computed(() => {
  if (syncing.value) {
    return '同步中';
  }
  if (syncError.value) {
    return '失败';
  }
  if (syncResult.value) {
    return '已完成';
  }
  return '待执行';
});

const syncStatusTag = computed(() => {
  if (syncError.value) {
    return 'danger';
  }
  if (syncResult.value) {
    return 'success';
  }
  return 'info';
});

const summaryCards = computed(() => {
  const local = localSummary.value || {};
  const remote = remoteSummary.value || {};
  return [
    { key: 'localTotal', label: '本机总数', value: local.total ?? '-' },
    { key: 'remoteTotal', label: '对端总数', value: remote.total ?? '-' },
    { key: 'localPending', label: '本机待确认', value: local.uncachedFilialeCount ?? '-' },
    { key: 'remotePending', label: '对端待确认', value: remote.uncachedFilialeCount ?? '-' },
  ];
});

const summaryRows = computed(() => {
  const rows = [];
  if (localSummary.value) {
    rows.push(normalizeSummaryRow('本机', localSummary.value));
  }
  if (remoteSummary.value) {
    rows.push(normalizeSummaryRow('对端', remoteSummary.value));
  }
  return rows;
});

function normalizeSummaryRow(name, summary) {
  return {
    name,
    total: summary.total ?? 0,
    northAmericaCount: summary.northAmericaCount ?? 0,
    nonNorthAmericaCount: summary.nonNorthAmericaCount ?? 0,
    uncachedFilialeCount: summary.uncachedFilialeCount ?? summary.northAmericaUncachedCount ?? 0,
    snapshotHash: summary.snapshotHash || '',
  };
}

function formatBlacklistSyncError(error) {
  const data = error?.response?.data || {};
  const message = data.msg || error?.message || '同步失败';
  const details = [];
  if (data.peerStatus) {
    details.push(`peerStatus=${data.peerStatus}`);
  }
  if (data.peerMsg) {
    details.push(`peerMsg=${data.peerMsg}`);
  } else if (data.peerBody) {
    details.push(`peerBody=${data.peerBody}`);
  }
  return details.length ? `${message} (${details.join(', ')})` : message;
}

async function handleSyncBlacklist() {
  try {
    await ElMessageBox.confirm('确认开始同步国内/海外锁号？', '同步锁号', {
      confirmButtonText: '开始同步',
      cancelButtonText: '取消',
      type: 'warning',
    });
  } catch (error) {
    return;
  }

  syncing.value = true;
  syncError.value = '';
  try {
    const response = await syncFordBlacklistServers();
    syncResult.value = response.data || null;
    ElMessage.success('国内/海外锁号同步完成');
  } catch (error) {
    syncResult.value = null;
    syncError.value = formatBlacklistSyncError(error);
    ElMessage.error(syncError.value);
  } finally {
    syncing.value = false;
  }
}

async function handleRefreshBlackSnConfigs() {
  configRefreshing.value = true;
  try {
    const response = await refreshBlackSnConfigs(50);
    const result = response.data || {};
    const selected = result.selected ?? 0;
    if (result.started) {
      ElMessage.success(`已启动后台补齐，本轮 ${selected} 个 SN`);
    } else if (result.alreadyRunning) {
      ElMessage.info(`已有补齐任务正在运行，当前候选 ${selected} 个 SN`);
    } else {
      ElMessage.info('当前没有需要补齐的黑名单接头配置');
    }
  } catch (error) {
    const message = error?.response?.data?.msg || error?.message || '后台补齐启动失败';
    ElMessage.error(message);
  } finally {
    configRefreshing.value = false;
  }
}
</script>

<style scoped>
.redis-admin-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.section-heading,
.operation-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-heading {
  margin-bottom: 14px;
}

.section-heading h4 {
  font-size: 16px;
}

.operation-row {
  align-items: center;
  padding: 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  background: #f7fbff;
}

.operation-row + .operation-row {
  margin-top: 10px;
}

.operation-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #25415f;
  font-weight: 800;
}

.operation-title .el-icon {
  color: #2563eb;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.sync-error {
  margin-top: 12px;
}

.summary-card {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e0e9f3;
  border-radius: 8px;
  background: #ffffff;
}

.summary-card span,
.summary-card strong {
  display: block;
}

.summary-card span {
  color: #6b7b8f;
  font-size: 12px;
}

.summary-card strong {
  margin-top: 5px;
  color: #132033;
  font-size: 22px;
  line-height: 1;
}

.summary-table {
  width: 100%;
  margin-top: 14px;
}

.summary-table :deep(.el-table__header th) {
  background: #f6f9fc !important;
  color: #4b5f75;
  font-weight: 800;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

@media (max-width: 760px) {
  .section-heading,
  .operation-row {
    flex-direction: column;
    align-items: stretch;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 520px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
